import axios from 'axios'
import { httpService } from './http.service'

/**
 * The user auth service performs actions to sign-in and sig-out of the system.
 *  Most of the methods here return a new user object which represents either
 *  and unauthenticated user (pre-login or post-logout) or an authenticated
 *  user. A new user object is returned as a value and isn't meant to be mod-
 *  ified after creation. Changes to the state of the user's sesison are re-
 *  flected by creating a new user object (upon token refresh).
 * This user object is supplied to the userContext as a value so changes propagate
 *  to all context consumers.
 */

// Initialize an un-intercepted axios service (bypass default behavior on unauthorized reqs)
const authAxios = axios.create()
authAxios.defaults.baseURL = httpService.getBaseURL()
authAxios.defaults.withCredentials = true

// Base user class represents an unauthenticated user/session
class UnauthenticatedUser {
  constructor (authError, redirect) {
    // If this user is created as a result of a failed authentication attempt,
    //  store the error response for evalutaion
    if (authError) {
      this._authError = authError
    }
    // If this user is created as a result of a logout event, the
    //  redirect parameter contains the destination url
    if (redirect) {
      this._redirect = redirect
    }
  }

  isAuthenticated () { return false }
  getToken () { return '' }
  userIsInAuthGroup () { return false }
  isSiteAdmin () { return false }
  isUserAdmin () { return false }
  usernameMatches () { return false }
  hasPermission () { return false }

  get displayName () { return '' }
  get username () { return '' }
  get accessToken () { return '' }
  get permissions () { return [] }
  get userGroups () { return [] }
  get authGroups () { return [] }
  get tokenExpiration () { return 0 }
  get authError () { return this._authError }
  get redirect () { return this._redirect }

  shouldUpdateProfile () {
    return this._authError && this._authError.response && this._authError.response.status === 412
  }
}

// An AuthenticatedUser represents a logged in user/session
class AuthenticatedUser extends UnauthenticatedUser {
  constructor (authData) {
    super()

    // Initialize the user object based on the user reponse from the API
    this._username = authData.user.userId || authData.user.id
    this._authGroups = authData.user.authGroups || []
    this._userGroups = authData.userGroups || []
    this._permissions = authData.permissions || []
    this._accessToken = authData.token
    this._displayName = authData.user.name
    this._tokenExpiration = authData.user.expiration
    this._provider = authData.user.provider
    this._providerId = authData.user.providerId
  }

  isAuthenticated () { return true }
  getToken () { return this._accessToken }
  userIsInAuthGroup (groupName) { return this._authGroups.includes(groupName) }
  isSiteAdmin () { return this.userIsInAuthGroup('SiteAdmin') }
  isUserAdmin () { return this.hasPermission('users:admin') }
  usernameMatches (username) { return this._username === username }
  hasPermission (permission) { return this._permissions.includes(permission) }

  get displayName () { return this._displayName }
  get username () { return this._username }
  get accessToken () { return this._accessToken }
  get permissions () { return this._permissions }
  get userGroups () { return this._userGroups }
  get authGroups () { return this._authGroups }
  get tokenExpiration () { return this._tokenExpiration }
}

// User authentication service handles interaction with the API
//  Methods return a user object of some sort (or throw)
class UserAuthService {
  constructor () {
    this._authProviders = []
  }

  // Store information about the supported id providers
  setAuthProviders (providers) {
    this._authProviders = providers
  }

  getAuthProviders () {
    return this._authProviders
  }

  getDefaultProvider () {
    let provider = null
    this._authProviders.forEach((p) => {
      if (p.default) {
        provider = p
      }
    })
    return provider
  }

  getDefaultLoginUrl () {
    const p = this.getDefaultProvider()
    return (p !== null && p.loginUrl) || '/login'
  }

  resetUser (authError) {
    return new UnauthenticatedUser(authError)
  }

  // Return a user object which reflects the results of the api/auth/user check
  async checkServerIfAuthenticated () {
    try {
      const { data } = await authAxios.get('api/auth/user')
      return new AuthenticatedUser(data)
    } catch {
      return new UnauthenticatedUser()
    }
  }

  // Given an authCode and OAuth2 state, return a user object representing the
  //  result of a Cognito OAuth2 authentication
  async authenticateOAuth2 (authCode, state) {
    if (!authCode) {
      // This is a proper fatal error (probably due to a coding bug)
      //  Check for existence of authCode before attempting to call this
      //  method.
      throw Error('Require authCode to continue authentication')
    }

    let url = 'api/auth/oauth2?code=' + authCode
    if (state) {
      url += '&state=' + state
    }

    try {
      const { data } = await authAxios.get(url)

      // The API seems to be returning 200 even in the case of an error, that's why we need to check for data.user to be present.
      if (!data.user) {
        return new UnauthenticatedUser(Error('User session not available'))
      }

      return new AuthenticatedUser(data)
    } catch (error) {
      return new UnauthenticatedUser(error)
    }
  }

  // Given a username and password (and optional profile data), return a user
  //  object representing the result of a Cognito User Pool login
  async authenticateUserPass (username, password, { newPassword, firstName, lastName } = {}) {
    let form = {
      username,
      password
    }

    if (newPassword) {
      form.newpassword = newPassword
      form.given_name = firstName
      form.family_name = lastName
    }

    try {
      const { data } = await authAxios.post('api/auth/login', form)

      // The API seems to be returning 200 even in the case of an error, that's why we need to check for data.user to be present.
      if (!data.user) {
        return new UnauthenticatedUser(Error('User session not available'))
      }

      return new AuthenticatedUser(data)
    } catch (error) {
      return new UnauthenticatedUser(error)
    }
  }

  // Given an access token, return a user object representing the result of
  //  a bearer token authentication
  async authenticateToken (act, reportid) {
    if (!act) {
      throw Error('Require Access Token to continue authentication')
    }

    let url = 'api/auth/login-accesstoken?accesstoken=' + act
    if (reportid) {
      url += '&report=/reports/' + reportid + '/view'
    }

    try {
      const { data } = await axios.get(url)

      // The API seems to be returning 200 even in the case of an error, that's why we need to check for data.user to be present.
      if (!data.user) {
        return new UnauthenticatedUser(Error('User session not available'))
      }

      return new AuthenticatedUser(data)
    } catch (error) {
      return new UnauthenticatedUser(error)
    }
  }

  async signout () {
    const result = await authAxios.post('api/auth/logout', {})
    let redirect = null
    if (result.data && result.data.redirect) {
      redirect = result.data.redirect
    }
    return new UnauthenticatedUser(null, redirect)
  }

  // Helper methods for password reset/update
  resetPassword (username) {
    return authAxios.post('api/auth/forgot-password', { username })
  }

  updatePassword (username, password, code) {
    return authAxios.post('api/auth/verify', {
      username,
      new_password: password,
      mfacode: code
    })
  }
}

export const userAuthService = new UserAuthService()
